/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Setter } from "hooks/state/syncing";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { createContext, ReactNode, use, useMemo } from "react";

export interface LabellingHotkeys {
  deleteEvent: string;
  deleteEventAndJumpToStart: string;
  jumpToTimestamp: string;

  forceSave: string;
  nextVideo: string;
  manuallyPopulateQueue: string;

  flip: string;
  togglePlayback: string;
  skipBack: string;
  skipForward: string;
  speedUp: string;

  labelMix: string;
  labelShredded: string;
  labelTurnings: string;
  labelTireWire: string;
  labelReclaim: string;
  labelCastIronBorings: string;
  labelTankTracks: string;
  labelRebar: string;
  labelBillets: string;
  labelWireBundles: string;
  labelFlashings: string;
  labelUnknown: string;
  labelObscured: string;
  labelInvalid: string;

  labelReviewRequired: string;
}

export const defaultHotkeys: LabellingHotkeys = {
  deleteEvent: "backspace",
  deleteEventAndJumpToStart: "shift+backspace",
  jumpToTimestamp: "=",

  forceSave: "comma",
  nextVideo: ".",
  manuallyPopulateQueue: "shift+.",

  flip: ";",
  togglePlayback: "up,space",
  skipBack: "left",
  skipForward: "right",
  speedUp: "down",

  labelMix: "s",
  labelShredded: "w",
  labelTurnings: "a",
  labelTireWire: "shift+a",
  labelReclaim: "q",
  labelCastIronBorings: "x",
  labelTankTracks: "shift+d",
  labelRebar: "d",
  labelBillets: "b",
  labelWireBundles: "c",
  labelFlashings: "shift+c",
  labelUnknown: "u",
  labelObscured: "/",
  labelInvalid: "e",

  labelReviewRequired: "r",
};

const Context = createContext<
  [Partial<LabellingHotkeys>, Setter<Partial<LabellingHotkeys>>]
>([{}, () => null]);

export const ProvideLabellingHotkeys = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [hotkeys, setHotkeys] = useLocalStorageState<Partial<LabellingHotkeys>>(
    "labellingHotkeys",
    {}
  );

  return (
    <Context
      value={useMemo(() => [hotkeys, setHotkeys], [hotkeys, setHotkeys])}
    >
      {children}
    </Context>
  );
};

export const useLabellingHotkey = (
  name: keyof LabellingHotkeys
): [string, Setter<string>] => {
  const [hotkeys, setHotkeys] = use(Context);

  return [
    hotkeys[name] ?? defaultHotkeys[name] ?? "",
    (hotkey) => {
      setHotkeys((current) => ({ ...current, [name]: hotkey }));
    },
  ];
};
